/**
 * One student's academic performance, for their teachers and school.
 *
 * Reached from a class or session results table. Access is decided by
 * firestore.rules (canAccessStudent): a teacher sees students in classes they
 * manage, a school admin or principal sees their own school. A denied read
 * shows as "not available", never someone else's data.
 */

import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { ArrowLeft } from 'lucide-react';
import { db } from '../../config/firebase';
import { StudentPerformance } from '../../Components/analytics/StudentPerformance';
import { AnalyticsEmptyState, AnalyticsPanel } from '../../Components/analytics/AnalyticsPanel';
import { learnXRFontStyle } from '../../Components/LearnXRTypography';
import { Skeleton } from '../../Components/ui/skeleton';
import { getPassThreshold, getStudentLaunches, getStudentResults, type LaunchRow } from '../../services/performanceService';
import { DEFAULT_PASS_THRESHOLD, type Result } from '../../lib/analytics/metrics';

interface StudentInfo {
  name: string;
  email: string | null;
  schoolId: string | null;
  classNames: string[];
}

const StudentAnalyticsPage = () => {
  const { studentId } = useParams<{ studentId: string }>();
  const navigate = useNavigate();
  const [student, setStudent] = useState<StudentInfo | null>(null);
  const [results, setResults] = useState<Result[]>([]);
  const [launches, setLaunches] = useState<LaunchRow[]>([]);
  const [passThreshold, setPassThreshold] = useState(DEFAULT_PASS_THRESHOLD);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (!studentId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const snap = await getDoc(doc(db, 'users', studentId));
        if (!snap.exists() || snap.data()?.role !== 'student') {
          if (!cancelled) setNotFound(true);
          return;
        }
        const u = snap.data();
        const schoolId: string | null = u.school_id ?? null;
        const classIds: string[] = Array.isArray(u.class_ids) ? u.class_ids : [];
        const [classSnaps, scoreList, launchList, threshold] = await Promise.all([
          Promise.all(classIds.slice(0, 5).map((id) => getDoc(doc(db, 'classes', id)).catch(() => null))),
          getStudentResults(studentId, schoolId),
          getStudentLaunches(studentId, schoolId).catch(() => [] as LaunchRow[]),
          getPassThreshold(schoolId),
        ]);
        if (cancelled) return;
        setStudent({
          name: u.name || u.displayName || u.display_name || u.email || 'Student',
          email: u.email ?? null,
          schoolId,
          classNames: classSnaps.filter((c) => c?.exists()).map((c) => c!.data()?.class_name || 'Class'),
        });
        setResults(scoreList);
        setLaunches(launchList);
        setPassThreshold(threshold);
      } catch (err) {
        console.error('[StudentAnalyticsPage] load failed:', err);
        if (!cancelled) {
          if ((err as { code?: string })?.code === 'permission-denied') setNotFound(true);
          else setError('This student’s results could not be loaded. Please try again.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [studentId]);

  return (
    <div className="min-h-screen bg-background pb-12 pt-24">
      <div className="mx-auto max-w-6xl space-y-5 px-4 sm:px-6">
        <button type="button" onClick={() => navigate(-1)} className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground">
          <ArrowLeft className="h-3.5 w-3.5" /> Back
        </button>

        {notFound ? (
          <AnalyticsPanel title="Student not available">
            <AnalyticsEmptyState
              title="This student doesn’t exist or isn’t in your classes"
              description="Teachers see students in the classes they manage; schools see their own students."
            />
          </AnalyticsPanel>
        ) : (
          <>
            <header>
              <p className="text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">Student performance</p>
              {student ? (
                <>
                  <h1 className="mt-1 text-2xl font-bold text-foreground" style={learnXRFontStyle}>
                    {student.name}
                  </h1>
                  <p className="mt-1 text-sm text-muted-foreground">
                    {[student.email, student.classNames.join(', ')].filter(Boolean).join(' · ')}
                  </p>
                </>
              ) : (
                <Skeleton className="mt-2 h-7 w-56" />
              )}
            </header>
            <StudentPerformance results={results} launches={launches} audience="staff" passThreshold={passThreshold} loading={loading} error={error} />
          </>
        )}
      </div>
    </div>
  );
};

export default StudentAnalyticsPage;
